import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { withCache } from '@ngneat/cashew';

import { API, BomExportPath } from '@cdba/shared/constants/api';
import { HttpParamsEncoder } from '@cdba/shared/http';
import { ReferenceTypeIdentifier } from '@cdba/shared/models/reference-type-identifier.model';
import { LocalStorageService } from '@cdba/shared/services';

import {
  FilterItem,
  FilterItemIdValue,
  FilterItemIdValueUpdate,
  FilterItemRange,
  FilterItemRangeUpdate,
  FilterItemType,
  SearchResult,
} from '../../core/store/reducers/search/models';
import { InitialFiltersResponse } from '../initial-filters-response.model';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  private readonly httpClient = inject(HttpClient);
  private readonly localStorageService = inject(LocalStorageService);

  private readonly PARAM_LANGUAGE = 'language';
  private readonly PARAM_SEARCH_FOR = 'search_for';

  private readonly INITIAL_FILTER = 'initial-filter';
  private readonly SEARCH = 'search';
  private readonly POSSIBLE_FILTER = 'possible-filter';

  getInitialFilters(): Observable<FilterItem[]> {
    return this.httpClient
      .get<InitialFiltersResponse>(`${API.v1}/${this.INITIAL_FILTER}`, {
        context: withCache(),
      })
      .pipe(map((response) => response.items));
  }

  search(filters: FilterItem[]): Observable<SearchResult> {
    const params: HttpParams = new HttpParams().set(
      this.PARAM_LANGUAGE,
      this.localStorageService.getItem<string>('language', false)
    );

    const payload = this.prepareSearchPayload(filters);

    return this.httpClient.post<SearchResult>(
      `${API.v1}/${this.SEARCH}`,
      {
        filters: payload,
      },
      { params }
    );
  }

  autocomplete(
    textSearch: string,
    filterName: string
  ): Observable<FilterItemIdValue> {
    const params = new HttpParams({ encoder: new HttpParamsEncoder() }).set(
      this.PARAM_SEARCH_FOR,
      textSearch
    );

    return this.httpClient.get<FilterItemIdValue>(
      `${API.v1}/${this.POSSIBLE_FILTER}/${filterName}`,
      {
        params,
        context: withCache(),
      }
    );
  }

  textSearch(_textSearch: any): Observable<SearchResult> {
    return of(new SearchResult([], [], 0));
  }

  requestBomExport(
    referenceTypesIdentifiers: ReferenceTypeIdentifier[]
  ): Observable<HttpResponse<void>> {
    const path = `${API.v1}/${BomExportPath}`;

    return this.httpClient.post<void>(path, referenceTypesIdentifiers, {
      observe: 'response',
    });
  }

  private prepareSearchPayload(
    filters: FilterItem[]
  ): (FilterItemIdValueUpdate | FilterItemRangeUpdate)[] {
    return filters
      .map((filter) => {
        switch (filter.type) {
          case FilterItemType.ID_VALUE: {
            const ids = (filter as FilterItemIdValue).selectedItems.map(
              (item) => item.id as string
            );

            return new FilterItemIdValueUpdate(filter.name, ids);
          }
          case FilterItemType.RANGE: {
            return new FilterItemRangeUpdate(
              filter.name,
              (filter as FilterItemRange).minSelected,
              (filter as FilterItemRange).maxSelected
            );
          }
          default: {
            return undefined;
          }
        }
      })
      .filter((item) => item !== undefined);
  }
}
